import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Asterisk, Stethoscope } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

import { useDebounce, useDoctors, usePatient } from "@/hooks";
import { useCheckPecos } from "@/hooks/useCheckPecos";
import { useMultiStepFormStore } from "@/stores";
import type { DoctorInformationFormType } from "@/types";
import { doctorInformationSchema } from "@/types";
import { Checkbox, ComboboxWithLabels, FormField, Input } from "@/ui";
import { NotQualifyModal } from "@/ui/common/NotQualifyModal";
import { PATIENT_DISQUALIFY_REASONS, STATE_OPTIONS } from "@/utils";
import { DoctorCombobox } from "./DoctorCombobox";
import FormButtons from "./FormButtons";

const DoctorInformationForm = () => {
  const [search, setSearch] = useState("");
  const [isNotQualifyModalOpen, setIsNotQualifyModalOpen] = useState(false);

  const { useDisqualifyPatient } = usePatient();

  const { mutate: disqualifyPatient, isPending } = useDisqualifyPatient();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
    watch,
  } = useForm<DoctorInformationFormType>({
    resolver: zodResolver(doctorInformationSchema),
    defaultValues: { hasGoogleMatch: false },
  });
  const { setMultiStepFormData, goToNextFormStep, multiStepFormData } =
    useMultiStepFormStore();

  const onSubmit: SubmitHandler<DoctorInformationFormType> = (data) => {
    if (pecosCheck !== undefined && !pecosCheck.isEnrolled) {
      return setIsNotQualifyModalOpen(true);
    }
    setMultiStepFormData({
      doctorInformationFormData: data,
    });
    goToNextFormStep();
  };
  const selectedDoctor = watch("doctor") || {};

  const debouncedSearch = useDebounce(search);

  const { data: doctors = [], isLoading } = useDoctors(
    debouncedSearch,
    debouncedSearch.length > 0,
  );

  const { data: pecosCheck } = useCheckPecos(
    selectedDoctor?.npi,
    !!selectedDoctor?.npi,
  );
  useEffect(() => {
    if (multiStepFormData?.doctorInformationFormData) {
      reset(multiStepFormData.doctorInformationFormData);
    }
  }, [multiStepFormData, reset]);

  useEffect(() => {
    if (pecosCheck !== undefined && !pecosCheck.isEnrolled) {
      setIsNotQualifyModalOpen(true);
    } else {
      setIsNotQualifyModalOpen(false);
    }
  }, [pecosCheck]);

  const handleEndIntake = () => {
    if (multiStepFormData?.generalInformationFormData) {
      const { generalInformationFormData } = multiStepFormData;
      const { firstName, lastName, dateOfBirth, email } =
        generalInformationFormData;
      disqualifyPatient({
        firstName,
        lastName,
        dateOfBirth,
        email,
        reasonToDisqualify: PATIENT_DISQUALIFY_REASONS.NOT_PECOS,
      });
    }
  };

  return (
    <div className="flex flex-col gap-y-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Stethoscope className="text-primary-600" size={16} />
          <p className="font-extrabold">Doctor Information</p>
        </div>
        <p className="flex text-sm text-neutral-500">
          Required fields (<Asterisk className="text-primary-600" size={12} />)
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField
          label="Search for a Doctor"
          error={undefined}
          className="col-span-2"
        >
          <Controller
            control={control}
            name="doctor"
            render={({ field }) => (
              <DoctorCombobox
                value={selectedDoctor}
                placeholder="Select or search by name or NPI"
                onChange={(selectedDoctor) => {
                  if (!selectedDoctor) {
                    clearErrors();
                  }
                  field.onChange(selectedDoctor);
                  setValue(
                    "doctor.phoneNumber",
                    selectedDoctor?.phoneNumber ?? "",
                  );
                  setValue("doctor.faxNumber", selectedDoctor?.faxNumber ?? "");
                  setValue(
                    "doctor.addressLineOne",
                    selectedDoctor?.addressLineOne ?? "",
                  );
                  setValue(
                    "doctor.addressLineTwo",
                    selectedDoctor?.addressLineTwo ?? "",
                  );
                  setValue("doctor.city", selectedDoctor?.city ?? "");
                  setValue("doctor.state", selectedDoctor?.state ?? "");
                  setValue("doctor.zipCode", selectedDoctor?.zipCode ?? "");
                }}
                hasError={Boolean(errors.doctor)}
                onSearchChange={setSearch}
                isLoading={isLoading}
                options={doctors}
              />
            )}
          />
        </FormField>
        {selectedDoctor?.npi ? (
          <>
            <FormField
              label="Phone Number"
              error={errors.doctor?.phoneNumber?.message}
              hasAsterisk
            >
              <Input
                {...register("doctor.phoneNumber")}
                error={errors.doctor?.phoneNumber?.message}
                placeholder="(XXX) XXX-XXXX"
                disabled={true}
              />
            </FormField>

            <FormField
              label="Fax Number"
              error={errors.doctor?.faxNumber?.message}
            >
              <Input
                {...register("doctor.faxNumber")}
                error={errors.doctor?.faxNumber?.message}
                placeholder="(XXX) XXX-XXXX"
              />
            </FormField>

            <FormField
              label="Address Line 1"
              error={errors.doctor?.addressLineOne?.message}
              hasAsterisk
            >
              <Input
                {...register("doctor.addressLineOne")}
                error={errors.doctor?.addressLineOne?.message}
                placeholder="Street address"
              />
            </FormField>

            <FormField
              label="Address Line 2"
              error={errors.doctor?.addressLineTwo?.message}
            >
              <Input
                {...register("doctor.addressLineTwo")}
                error={errors.doctor?.addressLineTwo?.message}
                placeholder="Apt, Suite, etc."
              />
            </FormField>

            <FormField
              label="City"
              error={errors.doctor?.city?.message}
              hasAsterisk
            >
              <Input
                {...register("doctor.city")}
                error={errors.doctor?.city?.message}
                placeholder="City"
              />
            </FormField>

            <FormField
              label="State"
              error={errors.doctor?.state?.message}
              hasAsterisk
            >
              <Controller
                control={control}
                name="doctor.state"
                render={({ field, fieldState }) => (
                  <ComboboxWithLabels
                    options={STATE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    hasError={!!fieldState.error?.message}
                    placeholder="Select or search state"
                  />
                )}
              />
            </FormField>

            <FormField
              label="ZIP Code"
              error={errors.doctor?.zipCode?.message}
              hasAsterisk
            >
              <Input
                {...register("doctor.zipCode")}
                error={errors.doctor?.zipCode?.message}
                placeholder="12345"
              />
            </FormField>

            <FormField
              error={errors.hasGoogleMatch?.message}
              className="col-span-2"
            >
              <div className="flex items-center gap-2">
                <Controller
                  name="hasGoogleMatch"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      hasError={!!errors.hasGoogleMatch?.message}
                    />
                  )}
                />
                <label htmlFor="hasGoogleMatch">
                  I have reviewed the doctor&#x2019;s information and verified
                  it matches Google.
                </label>
              </div>
            </FormField>
          </>
        ) : null}
        <FormButtons />
      </form>
      <NotQualifyModal
        show={isNotQualifyModalOpen}
        title="Doctor is not PECOS certified"
        description="Patient does not qualify."
        onClose={() => setIsNotQualifyModalOpen(false)}
        handleEndIntake={handleEndIntake}
        isLoading={isPending}
      />
    </div>
  );
};

export default DoctorInformationForm;
